import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hoursToMilliseconds } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { ApiService } from 'api';
import { SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS } from 'common/constants/services';
import { RISK_FREE_RATES_QUERY_KEY } from 'common/constants/services/allocations';
import { GetRiskFreeRatesParams } from './useGetRiskFreeRatesByMarketDate.types';
import { DISPLAY_DATE_FORMAT } from '../../../../pages/Financials/data';

const useGetRiskFreeRatesByMarketDate = (params: GetRiskFreeRatesParams) => {
  const { marketDate, OPMInputs, shouldQueryAutomatically = true } = params;

  const queryFn = useCallback(() => {
    const promiseMap = OPMInputs.reduce((sofar, opmInput) => {
      const date = opmInput.backsolveDate ?? marketDate;
      const promise = ApiService.apiObservationsRead(
        opmInput.maturity.toString(),
        moment(date).format(DISPLAY_DATE_FORMAT)
      );
      return {
        ...sofar,
        [opmInput.scenarioIdOrRef]: promise,
      };
    }, {} as { [key: string]: Promise<number> });
    return Promise.all(Object.values(promiseMap)).then(results =>
      results.reduce((sofar, result, index) => {
        const key = Object.keys(promiseMap)[index];
        return {
          ...sofar,
          [key]: result,
        };
      }, {})
    );
  }, [OPMInputs, marketDate]);

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(marketDate) && !isEmpty(OPMInputs) && shouldQueryAutomatically,
    queryFn: () => queryFn(),
    queryKey: [RISK_FREE_RATES_QUERY_KEY, OPMInputs, marketDate],
    refetchOnWindowFocus: SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS,
    staleTime: hoursToMilliseconds(24),
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetRiskFreeRatesByMarketDate;
